import { getApiData, postFormData } from "../utils/APIHelper";

// Design bid flow — a design scope is priced via deliverables + payment
// groups instead of BOQs. `bidId` is the bid project's id (the same
// `project_id` used in the /rfq/design-pricing route). Moodboard endpoints
// key off the same id (the bid project id).
const DESIGN_BID_ENDPOINTS = {
    GET_DETAILS: (bidId) => `api/design-bid/${bidId}`,
    UPDATE: (bidId) => `api/design-bid/${bidId}/update`,
    SUBMIT: (bidId) => `api/design-bid/${bidId}/submit`,
    MOODBOARDS: (projectId) => `api/design-proposal/${projectId}/moodboards`,
    MOODBOARD: (projectId, moodboardId) =>
        `api/design-proposal/${projectId}/moodboards/${moodboardId}`,
};

/**
 * Get design bid details — project, proposal, designer, deliverables,
 * payments and moodboards.
 */
export const getDesignBidDetails = async (bidId) => {
    try {
        const response = await getApiData(
            DESIGN_BID_ENDPOINTS.GET_DETAILS(bidId),
            "GET",
            {},
        );
        return response;
    } catch (error) {
        console.error("Error fetching design bid details:", error);
        throw error;
    }
};

/**
 * Update a design bid — proposal data, deliverable milestones and/or
 * payment groups. Only the provided sections are sent.
 * @param {object} payload — { proposal_data, milestones, payments }
 */
export const updateDesignBid = async (bidId, payload = {}) => {
    try {
        const response = await getApiData(
            DESIGN_BID_ENDPOINTS.UPDATE(bidId),
            "PATCH",
            payload,
        );
        return response;
    } catch (error) {
        console.error("Error updating design bid:", error);
        throw error;
    }
};

/**
 * Submit the design proposal — moves the bid to awaiting-project-pricing.
 */
export const submitDesignBid = async (bidId) => {
    try {
        const response = await getApiData(
            DESIGN_BID_ENDPOINTS.SUBMIT(bidId),
            "POST",
            {},
        );
        return response;
    } catch (error) {
        console.error("Error submitting design bid:", error);
        throw error;
    }
};

/**
 * List moodboards for a design bid project.
 */
export const getDesignMoodboards = async (projectId) => {
    try {
        const response = await getApiData(
            DESIGN_BID_ENDPOINTS.MOODBOARDS(projectId),
            "GET",
            {},
        );
        return response?.images || [];
    } catch (error) {
        console.error("Error fetching moodboards:", error);
        throw error;
    }
};

/**
 * Upload moodboard files (images/PDFs, max 10 per request).
 */
export const uploadDesignMoodboards = async (projectId, files) => {
    try {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        const response = await postFormData(
            DESIGN_BID_ENDPOINTS.MOODBOARDS(projectId),
            formData,
        );
        return response;
    } catch (error) {
        console.error("Error uploading moodboards:", error);
        throw error;
    }
};

/**
 * Delete a single moodboard.
 */
export const deleteDesignMoodboard = async (projectId, moodboardId) => {
    try {
        const response = await getApiData(
            DESIGN_BID_ENDPOINTS.MOODBOARD(projectId, moodboardId),
            "DELETE",
            {},
        );
        return response;
    } catch (error) {
        console.error("Error deleting moodboard:", error);
        throw error;
    }
};
